import Phaser from "phaser";
import type { VehicleConfig, VehicleBodyStyle } from "../config/vehicles.ts";
import { BREAKABLE_PART_BALANCE, HARD_LANDING_BALANCE } from "../config/balance.ts";
import { ART_RES } from "../art/bake.ts";
import {
  driverHeadOffsetY,
  driverTorsoOffsetY,
  ensureJeepBodyTexture,
  ensureWheelTexture,
} from "../art/vehicleArt.ts";

export const TERRAIN_LABEL = "terrain";
export const WHEEL_LABEL = "wheel";
export const CHASSIS_LABEL = "chassis";
export const HEAD_LABEL = "driver-head";
export const TORSO_LABEL = "driver-torso";

export interface VehicleInput {
  gas: boolean;
  brake: boolean;
}

/**
 * A drivable vehicle: a chassis body + two wheel bodies on suspension springs, plus a
 * ragdoll-lite driver (head + torso) rigidly-ish attached via constraints. Wheels are
 * motorized by ramping their angular velocity toward a target each step (approximating a
 * torque-limited motor, since Matter has no first-class motor primitive). While airborne,
 * gas/brake instead apply a rotational torque to the chassis for air control.
 */
/** Closest a wheel centre may get to its mount, as a fraction of the suspension rest length. */
const WHEEL_MIN_TRAVEL = 0.25;
/** Max sideways wheel offset from its mount, as a fraction of the suspension rest length. */
const WHEEL_MAX_SWAY = 0.6;

export class Vehicle {
  readonly chassis: MatterJS.BodyType;
  readonly rearWheel: MatterJS.BodyType;
  readonly frontWheel: MatterJS.BodyType;
  readonly head: MatterJS.BodyType;
  readonly torso: MatterJS.BodyType;
  readonly config: VehicleConfig;

  /** Optional callback fired for ANY vehicle (not just breakable-part-enabled ones) on a
   * hard landing, used by `GameScene` to trigger camera shake + spark particles. */
  onHardLanding?: (impactSpeed: number) => void;

  private readonly scene: Phaser.Scene;
  private readonly graphics: Phaser.GameObjects.Graphics;
  private readonly rearWheelSprite: Phaser.GameObjects.Image;
  private readonly frontWheelSprite: Phaser.GameObjects.Image;
  /** Baked body + driver artwork; only for body styles that have it (see `vehicleArt.ts`). */
  private readonly bodySprite?: Phaser.GameObjects.Image;
  private rearGroundContacts = 0;
  private frontGroundContacts = 0;
  private crashed = false;

  /** Optional cosmetic breakable part (spoiler/bumper/tailgate); see `spawnBreakablePart`. */
  private breakablePart?: MatterJS.BodyType;
  private breakablePartConstraint?: MatterJS.ConstraintType;
  private breakablePartGraphics?: Phaser.GameObjects.Graphics;
  private breakablePartDetached = false;
  private wasGroundedForImpact = true;

  constructor(scene: Phaser.Scene, config: VehicleConfig, x: number, y: number, group: number) {
    this.scene = scene;
    this.config = config;

    const matter = scene.matter;
    const Body = matter.body;

    const collisionFilter: MatterJS.ICollisionFilter = { group, category: 1, mask: 0xffffffff };

    this.chassis = matter.bodies.rectangle(x, y, config.chassisWidth, config.chassisHeight, {
      label: CHASSIS_LABEL,
      collisionFilter,
      friction: 0.05,
      frictionAir: 0.01,
      density: 0.0025,
      chamfer: { radius: 8 },
    });
    Body.setCentre(
      this.chassis,
      { x: 0, y: config.centerOfMassOffsetY },
      true, // relative offset from geometric center
    );

    const wheelOptions: MatterJS.IBodyDefinition = {
      collisionFilter,
      friction: config.tireFriction,
      frictionAir: 0.01,
      density: 0.004,
      label: WHEEL_LABEL,
    };
    // Spawn wheels already extended to the suspension's rest length below the chassis
    // mounting point, so the spring constraint starts at (approximately) zero tension
    // instead of snapping taut on the very first physics step.
    const wheelSpawnY = y + config.wheelOffsetY + config.suspensionRestLength;
    this.rearWheel = matter.bodies.circle(
      x - config.wheelOffsetX,
      wheelSpawnY,
      config.wheelRadius,
      wheelOptions,
    );
    this.frontWheel = matter.bodies.circle(
      x + config.wheelOffsetX,
      wheelSpawnY,
      config.wheelRadius,
      wheelOptions,
    );

    // Ragdoll-lite driver: torso pinned just above the chassis, head pinned above the torso.
    // Both share the vehicle's collision group so they never collide with the chassis/wheels.
    // Anchor points and rest lengths are derived so the constraints start at (approximately)
    // their rest length instead of snapping violently into place on the first physics step.
    const torsoHalfHeight = 14;
    const headRadius = 11;
    const hipRestLength = 4;
    const neckRestLength = 4;

    const chassisTopY = y - config.chassisHeight / 2;
    const torsoY = chassisTopY - 22;
    const torsoTopY = torsoY - torsoHalfHeight - 10;
    const headY = torsoTopY - headRadius - neckRestLength - 6;

    this.torso = matter.bodies.rectangle(x, torsoY, 16, torsoHalfHeight * 2, {
      collisionFilter,
      label: TORSO_LABEL,
      density: 0.001,
      friction: 0.5,
    });
    this.head = matter.bodies.circle(x, headY, headRadius, {
      collisionFilter,
      label: HEAD_LABEL,
      density: 0.0006,
      friction: 0.5,
    });

    const suspensionOptions = {
      stiffness: config.suspensionStiffness,
      damping: config.suspensionDamping,
      length: config.suspensionRestLength,
    };
    const rearSpring = matter.add.constraint(
      this.chassis,
      this.rearWheel,
      config.suspensionRestLength,
      config.suspensionStiffness,
      {
        ...suspensionOptions,
        pointA: { x: -config.wheelOffsetX, y: config.wheelOffsetY },
      },
    );
    const frontSpring = matter.add.constraint(
      this.chassis,
      this.frontWheel,
      config.suspensionRestLength,
      config.suspensionStiffness,
      {
        ...suspensionOptions,
        pointA: { x: config.wheelOffsetX, y: config.wheelOffsetY },
      },
    );

    // Secondary "guide" links: a plain distance constraint only resists elongation along the
    // anchor-to-wheel line, so it lets the wheel swing sideways like a pendulum under drive
    // torque (which otherwise pulls the wheelbase out of shape and destabilizes the chassis).
    // A second, stiffer link from a horizontally-offset anchor forms a triangulated wishbone
    // that keeps the wheel roughly directly below its mount while still allowing vertical travel.
    const guideOffset = 16;
    const guideRestLength = Math.hypot(guideOffset, config.suspensionRestLength);
    const rearGuide = matter.add.constraint(this.chassis, this.rearWheel, guideRestLength, 0.7, {
      pointA: { x: -config.wheelOffsetX - guideOffset, y: config.wheelOffsetY },
      damping: 0.2,
    });
    const frontGuide = matter.add.constraint(this.chassis, this.frontWheel, guideRestLength, 0.7, {
      pointA: { x: config.wheelOffsetX + guideOffset, y: config.wheelOffsetY },
      damping: 0.2,
    });

    const torsoJoint = matter.add.constraint(this.chassis, this.torso, hipRestLength, 0.7, {
      pointA: { x: 0, y: -config.chassisHeight / 2 - 2 },
      pointB: { x: 0, y: torsoHalfHeight - 1 },
    });
    const neckJoint = matter.add.constraint(this.torso, this.head, neckRestLength, 0.9, {
      pointA: { x: 0, y: -torsoHalfHeight - 2 },
      pointB: { x: 0, y: headRadius + 2 },
    });

    matter.world.add([
      this.chassis,
      this.rearWheel,
      this.frontWheel,
      this.torso,
      this.head,
      rearSpring,
      frontSpring,
      rearGuide,
      frontGuide,
      torsoJoint,
      neckJoint,
    ]);

    // Depth order (back to front): struts/antenna graphics, wheels, body sprite. Vehicles
    // without baked body art still draw their chassis with Graphics, above the wheels.
    const wheelKey = ensureWheelTexture(scene, config.wheelRadius, config.accentColor);
    this.rearWheelSprite = scene.add.image(0, 0, wheelKey).setScale(1 / ART_RES).setDepth(10);
    this.frontWheelSprite = scene.add.image(0, 0, wheelKey).setScale(1 / ART_RES).setDepth(10);

    this.graphics = scene.add.graphics();
    if (config.bodyStyle === "jeep") {
      const art = ensureJeepBodyTexture(scene, config);
      this.bodySprite = scene.add
        .image(x, y, art.key)
        .setOrigin(art.originX, art.originY)
        .setScale(1 / ART_RES)
        .setDepth(11);
      this.graphics.setDepth(9);
    } else {
      this.graphics.setDepth(11);
    }

    if (config.breakablePartsEnabled) this.spawnBreakablePart(collisionFilter);

    this.setupGroundContactTracking();
    scene.matter.world.on("afterupdate", this.enforceWheelTravel, this);
  }

  /**
   * Bump stop + lateral limit for both wheels, run after every physics step. The spring and
   * guide links are pure distance constraints anchored at the same height, so a hard landing
   * can flip a wheel to the mirror-image solution *above* its mount (inside the body), where
   * both lengths are still satisfied and nothing pushes it back (wheels don't collide with
   * their own chassis). Clamping the wheel's position in the chassis frame prevents that.
   */
  private enforceWheelTravel(): void {
    const { wheelOffsetX, wheelOffsetY, suspensionRestLength } = this.config;
    const minY = wheelOffsetY + suspensionRestLength * WHEEL_MIN_TRAVEL;
    const maxDx = suspensionRestLength * WHEEL_MAX_SWAY;
    this.clampWheel(this.rearWheel, -wheelOffsetX, minY, maxDx);
    this.clampWheel(this.frontWheel, wheelOffsetX, minY, maxDx);
  }

  private clampWheel(wheel: MatterJS.BodyType, mountX: number, minY: number, maxDx: number): void {
    const c = this.chassis;
    const cos = Math.cos(c.angle);
    const sin = Math.sin(c.angle);
    const rx = wheel.position.x - c.position.x;
    const ry = wheel.position.y - c.position.y;
    const lx = rx * cos + ry * sin;
    const ly = -rx * sin + ry * cos;
    const clampedX = Phaser.Math.Clamp(lx, mountX - maxDx, mountX + maxDx);
    const clampedY = Math.max(ly, minY);
    if (clampedX === lx && clampedY === ly) return;

    const Body = this.scene.matter.body;
    const nrx = clampedX * cos - clampedY * sin;
    const nry = clampedX * sin + clampedY * cos;
    Body.setPosition(wheel, { x: c.position.x + nrx, y: c.position.y + nry });

    // Strip the wheel's velocity relative to the chassis along the clamped axes, so it
    // doesn't keep driving into the stop on the next step.
    const w = c.angularVelocity;
    const pvx = c.velocity.x - w * nry;
    const pvy = c.velocity.y + w * nrx;
    const dvx = wheel.velocity.x - pvx;
    const dvy = wheel.velocity.y - pvy;
    let lvx = dvx * cos + dvy * sin;
    let lvy = -dvx * sin + dvy * cos;
    if (clampedY !== ly && lvy < 0) lvy = 0;
    if (clampedX !== lx && Math.sign(lvx) === Math.sign(lx - mountX)) lvx = 0;
    Body.setVelocity(wheel, {
      x: pvx + lvx * cos - lvy * sin,
      y: pvy + lvx * sin + lvy * cos,
    });
  }

  /** Attaches a small decorative part (spoiler/bumper/tailgate) to the rear-top corner of
   * the chassis via a short, stiff constraint. It renders in world space (unlike the
   * chassis/wheels, which are drawn relative to the chassis transform each frame) so it can
   * keep rendering correctly after it detaches and becomes an independent physics body. */
  private spawnBreakablePart(collisionFilter: MatterJS.ICollisionFilter): void {
    const matter = this.scene.matter;
    const { chassisWidth: w, chassisHeight: h } = this.config;
    const anchor = { x: -w / 2 - 6, y: -h / 2 };
    const spawnPos = {
      x: this.chassis.position.x + anchor.x,
      y: this.chassis.position.y + anchor.y,
    };

    this.breakablePart = matter.bodies.rectangle(spawnPos.x, spawnPos.y, 26, 12, {
      collisionFilter,
      density: 0.0015,
      friction: 0.4,
      label: "breakable-part",
      chamfer: { radius: 3 },
    });
    this.breakablePartConstraint = matter.add.constraint(this.chassis, this.breakablePart, 0, 0.9, {
      pointA: anchor,
      pointB: { x: 0, y: 0 },
    });
    matter.world.add([this.breakablePart, this.breakablePartConstraint]);

    this.breakablePartGraphics = this.scene.add.graphics();
    this.breakablePartGraphics.setDepth(9);
  }

  private setupGroundContactTracking(): void {
    const matterWorld = this.scene.matter.world;
    matterWorld.on(
      "collisionstart",
      (event: Phaser.Physics.Matter.Events.CollisionStartEvent) => this.onCollision(event, 1),
    );
    matterWorld.on(
      "collisionend",
      (event: Phaser.Physics.Matter.Events.CollisionStartEvent) => this.onCollision(event, -1),
    );
  }

  private onCollision(
    event: Phaser.Physics.Matter.Events.CollisionStartEvent,
    delta: number,
  ): void {
    for (const pair of event.pairs) {
      const { bodyA, bodyB } = pair;
      const other = bodyA.label === TERRAIN_LABEL ? bodyB : bodyB.label === TERRAIN_LABEL ? bodyA : null;
      if (!other) continue;
      if (other === this.rearWheel) this.rearGroundContacts = Math.max(0, this.rearGroundContacts + delta);
      if (other === this.frontWheel) this.frontGroundContacts = Math.max(0, this.frontGroundContacts + delta);
    }
  }

  get isGrounded(): boolean {
    return this.rearGroundContacts > 0 || this.frontGroundContacts > 0;
  }

  get isRearGrounded(): boolean {
    return this.rearGroundContacts > 0;
  }

  get isFrontGrounded(): boolean {
    return this.frontGroundContacts > 0;
  }

  get speed(): number {
    const v = this.chassis.velocity;
    return Math.hypot(v.x, v.y);
  }

  markCrashed(): void {
    this.crashed = true;
  }

  get hasCrashed(): boolean {
    return this.crashed;
  }

  /** Applies gas/brake input for this physics step. Call once per Matter update. */
  applyInput(input: VehicleInput): void {
    if (this.crashed) return;
    const Body = this.scene.matter.body;
    const direction = input.gas ? 1 : input.brake ? -1 : 0;

    if (this.isGrounded) {
      const target = direction * this.config.maxWheelSpeed;
      // Ramp angular velocity toward the target, limited by engine torque (max change/step).
      const rampRear = this.rampToward(this.rearWheel.angularVelocity, target, this.config.engineTorque);
      const rampFront = this.rampToward(
        this.frontWheel.angularVelocity,
        target,
        this.config.engineTorque,
      );
      Body.setAngularVelocity(this.rearWheel, rampRear);
      Body.setAngularVelocity(this.frontWheel, rampFront);
    } else if (direction !== 0) {
      // Airborne: gas rotates nose up (backward), brake rotates nose down (forward). Chassis
      // angle increases clockwise (nose-down) in Phaser's y-down convention, so gas (direction
      // = +1) must apply a *negative* angular velocity change to pitch the nose up. Clamp the
      // resulting angular velocity so held input can't spiral into an uncontrollable flip.
      const maxAirAngularVelocity = 0.06;
      const next = this.chassis.angularVelocity - direction * this.config.airControlTorque;
      Body.setAngularVelocity(
        this.chassis,
        Phaser.Math.Clamp(next, -maxAirAngularVelocity, maxAirAngularVelocity),
      );
    }
  }

  private rampToward(current: number, target: number, maxDelta: number): number {
    const diff = target - current;
    if (Math.abs(diff) <= maxDelta) return target;
    return current + Math.sign(diff) * maxDelta;
  }

  /** Keeps the driver seated: the torso/head bodies are pinned to their seat positions in the
   * chassis' rotating frame every step. Rotating the offsets with the chassis matters: it puts
   * the head under the car when it flips over, so landing upside-down still knocks it out. */
  private syncDriverPose(): void {
    const Body = this.scene.matter.body;
    const { angle, position, velocity } = this.chassis;
    const sin = Math.sin(angle);
    const cos = Math.cos(angle);
    const place = (body: MatterJS.BodyType, localY: number) => {
      Body.setPosition(body, { x: position.x - sin * localY, y: position.y + cos * localY });
      Body.setAngle(body, angle);
      Body.setVelocity(body, velocity);
    };
    place(this.torso, driverTorsoOffsetY(this.config.chassisHeight));
    place(this.head, driverHeadOffsetY(this.config.chassisHeight));
  }

  /** Syncs the artwork to the current physics body transforms. */
  render(): void {
    this.syncDriverPose();
    this.placeWheelSprite(this.rearWheelSprite, this.rearWheel);
    this.placeWheelSprite(this.frontWheelSprite, this.frontWheel);

    const g = this.graphics;
    g.clear();
    if (this.bodySprite) {
      const { position, angle } = this.chassis;
      this.bodySprite.setPosition(position.x, position.y).setRotation(angle);
      this.drawSuspensionStruts(g);
      this.drawAntenna(g);
    } else {
      this.drawChassis(g);
      this.drawDriver(g);
    }

    this.detectHardLanding();
    this.renderBreakablePart();
  }

  private placeWheelSprite(sprite: Phaser.GameObjects.Image, wheel: MatterJS.BodyType): void {
    sprite.setPosition(wheel.position.x, wheel.position.y).setRotation(wheel.angle);
  }

  /** Converts a point in the chassis' local (centre-of-mass) frame to world space. */
  private chassisToWorld(localX: number, localY: number): { x: number; y: number } {
    const { angle, position } = this.chassis;
    const sin = Math.sin(angle);
    const cos = Math.cos(angle);
    return {
      x: position.x + cos * localX - sin * localY,
      y: position.y + sin * localX + cos * localY,
    };
  }

  /** Shock absorbers from the chassis mounts down to each wheel hub: a dark strut with a
   * chrome rod and a coil spring, so the suspension travel reads visually. */
  private drawSuspensionStruts(g: Phaser.GameObjects.Graphics): void {
    const { wheelOffsetX, wheelOffsetY } = this.config;
    for (const [side, wheel] of [
      [-1, this.rearWheel],
      [1, this.frontWheel],
    ] as const) {
      const mount = this.chassisToWorld(side * wheelOffsetX, wheelOffsetY - 14);
      const hub = wheel.position;
      const dx = hub.x - mount.x;
      const dy = hub.y - mount.y;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len;
      const ny = dx / len;

      g.lineStyle(9, 0x1a1a1a, 1);
      g.lineBetween(mount.x, mount.y, hub.x, hub.y);
      g.lineStyle(4, 0xb9bec6, 1);
      g.lineBetween(mount.x, mount.y, hub.x, hub.y);

      const coils = 6;
      g.lineStyle(2.5, 0xe0b22d, 1);
      g.beginPath();
      for (let i = 0; i <= coils * 2; i++) {
        const t = 0.1 + (i / (coils * 2)) * 0.55;
        const offset = (i % 2 === 0 ? -1 : 1) * 5.5;
        const px = mount.x + dx * t + nx * offset;
        const py = mount.y + dy * t + ny * offset;
        if (i === 0) g.moveTo(px, py);
        else g.lineTo(px, py);
      }
      g.strokePath();
    }
  }

  /** A whip antenna at the rear that sways with acceleration via a damped spring. */
  private antennaSway = 0;
  private antennaSwayVelocity = 0;
  private lastChassisVelocityX = 0;
  private drawAntenna(g: Phaser.GameObjects.Graphics): void {
    const accel = this.chassis.velocity.x - this.lastChassisVelocityX;
    this.lastChassisVelocityX = this.chassis.velocity.x;
    this.antennaSwayVelocity += -this.antennaSway * 0.12 - accel * 0.09;
    this.antennaSwayVelocity *= 0.86;
    this.antennaSway = Phaser.Math.Clamp(this.antennaSway + this.antennaSwayVelocity, -0.9, 0.9);

    const { chassisWidth: w, chassisHeight: h, centerOfMassOffsetY: com } = this.config;
    const baseLocal = { x: -w / 2 + 6, y: -h / 2 - com };
    const length = 52;
    const lean = -0.18 + this.antennaSway;
    const tipLocal = { x: baseLocal.x + Math.sin(lean) * length, y: baseLocal.y - Math.cos(lean) * length };
    const midLocal = {
      x: baseLocal.x + Math.sin(lean * 0.4) * length * 0.55,
      y: baseLocal.y - Math.cos(lean * 0.4) * length * 0.55,
    };
    const base = this.chassisToWorld(baseLocal.x, baseLocal.y);
    const mid = this.chassisToWorld(midLocal.x, midLocal.y);
    const tip = this.chassisToWorld(tipLocal.x, tipLocal.y);

    const curve = new Phaser.Curves.QuadraticBezier(
      new Phaser.Math.Vector2(base.x, base.y),
      new Phaser.Math.Vector2(mid.x, mid.y),
      new Phaser.Math.Vector2(tip.x, tip.y),
    );
    g.lineStyle(2.5, 0x1a1a1a, 1);
    curve.draw(g, 12);
    g.fillStyle(0x1a1a1a, 1);
    g.fillCircle(base.x, base.y, 3.5);
    g.fillStyle(0xe23b3b, 1);
    g.fillCircle(tip.x, tip.y, 3);
  }

  /** Detects the moment of landing (airborne → grounded transition) and, if this vehicle has
   * an attached breakable part, detaches it when the downward speed at landing exceeds
   * `BREAKABLE_PART_BALANCE.hardLandingVerticalSpeed`. Purely cosmetic — never affects
   * driving physics or ends the run (unlike `CrashDetector`, which watches the driver's head). */
  private detectHardLanding(): void {
    const grounded = this.isGrounded;
    if (!grounded) {
      this.wasGroundedForImpact = false;
      return;
    }
    if (!this.wasGroundedForImpact) {
      const impactSpeed = Math.abs(this.chassis.velocity.y);
      if (impactSpeed >= HARD_LANDING_BALANCE.minVerticalSpeed) this.onHardLanding?.(impactSpeed);
      if (
        this.breakablePart &&
        !this.breakablePartDetached &&
        impactSpeed >= BREAKABLE_PART_BALANCE.hardLandingVerticalSpeed
      ) {
        this.detachBreakablePart();
      }
    }
    this.wasGroundedForImpact = true;
  }

  /** Snaps off the breakable part's constraint so it becomes a free-tumbling physics body,
   * gives it a small kick so it visibly flies clear of the chassis, then schedules its
   * removal after `BREAKABLE_PART_BALANCE.detachedLifetimeSeconds`. */
  private detachBreakablePart(): void {
    if (!this.breakablePart || this.breakablePartDetached) return;
    this.breakablePartDetached = true;

    if (this.breakablePartConstraint) {
      this.scene.matter.world.remove(this.breakablePartConstraint);
      this.breakablePartConstraint = undefined;
    }

    const Body = this.scene.matter.body;
    Body.setVelocity(this.breakablePart, {
      x: this.chassis.velocity.x * 0.5 + (Math.random() - 0.5) * 4,
      y: -Math.abs(this.chassis.velocity.y) * 0.6 - 2,
    });
    Body.setAngularVelocity(this.breakablePart, (Math.random() - 0.5) * 0.3);

    this.scene.time.delayedCall(BREAKABLE_PART_BALANCE.detachedLifetimeSeconds * 1000, () => {
      if (this.breakablePart) this.scene.matter.world.remove(this.breakablePart);
      this.breakablePart = undefined;
      this.breakablePartGraphics?.destroy();
      this.breakablePartGraphics = undefined;
    });
  }

  private renderBreakablePart(): void {
    if (!this.breakablePart || !this.breakablePartGraphics) return;
    const g = this.breakablePartGraphics;
    g.clear();

    const { position, angle } = this.breakablePart;
    g.save();
    g.translateCanvas(position.x, position.y);
    g.rotateCanvas(angle);
    g.fillStyle(0x1a1a1a, 1);
    g.fillRoundedRect(-15, -8, 30, 16, 4);
    g.fillStyle(this.config.accentColor, 1);
    g.fillRoundedRect(-13, -6, 26, 12, 3);
    g.restore();
  }

  private drawChassis(g: Phaser.GameObjects.Graphics): void {
    const { position, angle } = this.chassis;
    const { chassisWidth: w, chassisHeight: h, color, accentColor, bodyStyle } = this.config;
    const outline = 0x1a1a1a;
    const outlineWidth = 4;

    g.save();
    g.translateCanvas(position.x, position.y);
    g.rotateCanvas(angle);

    // Rear bumper + front bumper (dark, chunky, slightly protruding).
    g.fillStyle(outline, 1);
    g.fillRoundedRect(-w / 2 - 6, h * 0.08, 10, h * 0.42, 3);
    g.fillRoundedRect(w / 2 - 4, h * 0.08, 10, h * 0.42, 3);

    // Main body: bold outline + flat cartoon fill.
    g.fillStyle(outline, 1);
    g.fillRoundedRect(-w / 2 - outlineWidth / 2, -h / 2 - outlineWidth / 2, w + outlineWidth, h + outlineWidth, 14);
    g.fillStyle(color, 1);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, 12);

    // Lower body accent stripe (cartoon shading, like the reference's darker underside).
    g.fillStyle(accentColor, 1);
    g.fillRoundedRect(-w / 2, h * 0.18, w, h * 0.32, 8);

    this.drawBodyTopper(g, w, h, outline, accentColor, bodyStyle);

    // Headlight + tail light dots for a friendly, readable silhouette.
    g.fillStyle(0xfff2a8, 1);
    g.fillCircle(w / 2 + 2, -h * 0.02, 5);
    g.lineStyle(2, outline, 1);
    g.strokeCircle(w / 2 + 2, -h * 0.02, 5);
    g.fillStyle(0xd94f4f, 1);
    g.fillCircle(-w / 2 - 2, -h * 0.02, 4);

    g.restore();
  }

  /** Draws the roof/cab-level silhouette detail that makes each `VehicleBodyStyle` read as
   * a distinct vehicle, on top of the shared bumper/body/stripe base drawn by
   * `drawChassis`. */
  private drawBodyTopper(
    g: Phaser.GameObjects.Graphics,
    w: number,
    h: number,
    outline: number,
    accentColor: number,
    bodyStyle: VehicleBodyStyle,
  ): void {
    const outlineWidth = 4;

    switch (bodyStyle) {
      case "bike": {
        // No cab: a handlebar stalk + a small saddle instead.
        const barX = w * 0.22;
        g.lineStyle(outlineWidth, outline, 1);
        g.lineBetween(barX, -h / 2 + 2, barX + 6, -h / 2 - 24);
        g.lineBetween(barX - 14, -h / 2 - 22, barX + 20, -h / 2 - 22);
        g.fillStyle(0x2a2a2a, 1);
        g.fillRoundedRect(-w * 0.18, -h / 2 - 10, w * 0.3, 10, 4);
        break;
      }
      case "truck": {
        // Cab up front + an open flatbed toward the rear.
        const cabTopY = -h / 2 - 26;
        g.lineStyle(outlineWidth, outline, 1);
        g.lineBetween(w * 0.06, -h / 2 + 2, w * 0.1, cabTopY);
        g.lineBetween(w * 0.42, -h / 2 + 2, w * 0.38, cabTopY);
        g.lineBetween(w * 0.1, cabTopY, w * 0.38, cabTopY);
        g.fillStyle(0x9fd8ff, 0.85);
        g.fillRoundedRect(w * 0.12, -h / 2 - 20, w * 0.24, 20, 3);
        g.fillStyle(outline, 1);
        g.fillRoundedRect(-w * 0.46, -h / 2 - 6, w * 0.4, 8, 2);
        break;
      }
      case "rally": {
        // Low, sleek roofline + a rear wing.
        const cabTopY = -h / 2 - 14;
        g.lineStyle(outlineWidth, outline, 1);
        g.lineBetween(-w * 0.24, -h / 2 + 2, -w * 0.1, cabTopY);
        g.lineBetween(w * 0.18, -h / 2 + 2, w * 0.12, cabTopY);
        g.lineBetween(-w * 0.1, cabTopY, w * 0.12, cabTopY);
        g.fillStyle(0x9fd8ff, 0.85);
        g.fillRoundedRect(-w * 0.08, -h / 2 - 10, w * 0.2, 10, 2);
        g.fillStyle(accentColor, 1);
        g.fillRect(-w / 2 - 4, -h / 2 - 16, 8, 18);
        g.fillRect(-w / 2 - 8, -h / 2 - 16, w * 0.24, 5);
        break;
      }
      case "monster": {
        // Tall roll cage arching over the cab.
        g.lineStyle(outlineWidth + 1, outline, 1);
        g.beginPath();
        g.arc(-w * 0.04, -h / 2, w * 0.32, Math.PI, 0, false);
        g.strokePath();
        g.lineBetween(-w * 0.36, -h / 2, -w * 0.36, -h / 2 + 6);
        g.lineBetween(w * 0.28, -h / 2, w * 0.28, -h / 2 + 6);
        break;
      }
      case "tractor": {
        // Tall narrow stack exhaust pipe + a simple canopy bar.
        g.fillStyle(0x2a2a2a, 1);
        g.fillRoundedRect(-w * 0.3, -h / 2 - 34, 8, 34, 2);
        g.fillStyle(0x1a1a1a, 1);
        g.fillCircle(-w * 0.3 + 4, -h / 2 - 34, 5);
        g.lineStyle(outlineWidth, outline, 1);
        g.lineBetween(w * 0.1, -h / 2 + 2, w * 0.14, -h / 2 - 20);
        g.lineBetween(w * 0.32, -h / 2 + 2, w * 0.28, -h / 2 - 20);
        g.lineBetween(w * 0.14, -h / 2 - 20, w * 0.28, -h / 2 - 20);
        break;
      }
      case "buggy": {
        // Smooth bubble canopy, evoking a hover-pod feel.
        g.fillStyle(0xffffff, 0.35);
        g.fillEllipse(-w * 0.02, -h / 2 - 8, w * 0.42, 22);
        g.lineStyle(2, outline, 0.8);
        g.strokeEllipse(-w * 0.02, -h / 2 - 8, w * 0.42, 22);
        break;
      }
      case "crab": {
        // Decorative extra "legs": short diagonal struts along the body, on top of the
        // two functional wheels, hinting at a six-legged crawler.
        g.lineStyle(6, outline, 1);
        for (const t of [-0.3, 0, 0.3]) {
          const legX = w * t;
          g.lineBetween(legX, h * 0.1, legX - 10, h * 0.5);
          g.lineBetween(legX, h * 0.1, legX + 10, h * 0.5);
        }
        g.lineStyle(3, accentColor, 1);
        for (const t of [-0.3, 0, 0.3]) {
          const legX = w * t;
          g.lineBetween(legX, h * 0.1, legX - 10, h * 0.5);
          g.lineBetween(legX, h * 0.1, legX + 10, h * 0.5);
        }
        break;
      }
      case "jeep":
      default: {
        // Open-top cab: windshield strut + roll bar (the original Starter Jeep look).
        const cabX = -w * 0.08;
        const cabTopY = -h / 2 - 22;
        g.lineStyle(outlineWidth, outline, 1);
        g.lineBetween(cabX - w * 0.22, -h / 2 + 2, cabX - w * 0.16, cabTopY);
        g.lineBetween(cabX + w * 0.3, -h / 2 + 2, cabX + w * 0.26, cabTopY);
        g.lineBetween(cabX - w * 0.16, cabTopY, cabX + w * 0.26, cabTopY);
        g.fillStyle(0x9fd8ff, 0.85);
        g.fillRoundedRect(cabX - w * 0.12, -h / 2 - 16, w * 0.3, 16, 3);
        break;
      }
    }
  }

  private drawDriver(g: Phaser.GameObjects.Graphics): void {
    const outline = 0x1a1a1a;
    const driverAngle = this.chassis.angle;

    g.save();
    g.translateCanvas(this.torso.position.x, this.torso.position.y + 4);
    g.rotateCanvas(driverAngle);
    g.fillStyle(outline, 1);
    g.fillRoundedRect(-9, -15, 18, 30, 5);
    g.fillStyle(0x3a6fb0, 1);
    g.fillRoundedRect(-8, -14, 16, 28, 4);
    g.restore();

    g.save();
    g.translateCanvas(this.head.position.x, this.head.position.y - 2);
    g.rotateCanvas(driverAngle);
    g.fillStyle(outline, 1);
    g.fillCircle(0, 0, 12);
    g.fillStyle(0xf1c27d, 1);
    g.fillCircle(0, 0, 10.5);
    g.fillStyle(0xc0392b, 1);
    g.fillRoundedRect(-11, -14, 22, 7, 3);
    g.fillRect(-2, -8, 15, 4);
    g.restore();
  }

  destroy(): void {
    this.scene.matter.world?.off("afterupdate", this.enforceWheelTravel, this);
    this.graphics.destroy();
    this.rearWheelSprite.destroy();
    this.frontWheelSprite.destroy();
    this.bodySprite?.destroy();
    this.scene.matter.world.remove([
      this.chassis,
      this.rearWheel,
      this.frontWheel,
      this.torso,
      this.head,
    ]);
    if (this.breakablePartConstraint) this.scene.matter.world.remove(this.breakablePartConstraint);
    if (this.breakablePart) this.scene.matter.world.remove(this.breakablePart);
    this.breakablePartGraphics?.destroy();
  }
}
